import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../../context/DarkModeContext';
import { 
  IoStarOutline, 
  IoStar, 
  IoCalendarOutline, 
  IoTrophyOutline,
  IoEllipsisVertical,
  IoCreateOutline,
  IoTrashOutline,
  IoExpandOutline,
  IoContractOutline
} from 'react-icons/io5';
import { Achievement } from '../../../hooks/useAchievements';

interface AchievementCardProps {
  achievement: Achievement;
  onEdit: () => void;
  onDelete: () => void;
  onToggleFeatured: () => void;
  isExpanded?: boolean;
}

const CATEGORY_COLORS = {
  technical: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
  leadership: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400' },
  collaboration: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
  innovation: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400' },
  impact: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' },
  growth: { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-600 dark:text-indigo-400' }
};

const AchievementCard: React.FC<AchievementCardProps> = ({
  achievement,
  onEdit,
  onDelete,
  onToggleFeatured,
  isExpanded: initialExpanded = false
}) => {
  const { darkMode } = useDarkMode();
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [showMenu, setShowMenu] = useState(false);

  // Style classes
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const hoverBg = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50';

  const categoryStyle = CATEGORY_COLORS[achievement.category as keyof typeof CATEGORY_COLORS] || CATEGORY_COLORS.technical;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  const getImpactStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i}>
          {i <= (achievement.impact_score || 3) ? (
            <IoStar className="w-4 h-4 text-yellow-500" />
          ) : (
            <IoStarOutline className="w-4 h-4 text-gray-400" />
          )}
        </span>
      );
    }
    return stars;
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`${cardBg} ${borderColor} border rounded-lg overflow-hidden ${hoverBg} transition-colors`}
    >
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className={`text-lg font-medium ${textColor}`}>{achievement.title}</h3>
              {achievement.is_featured && (
                <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 rounded-full text-xs">
                  <IoTrophyOutline className="w-3 h-3" />
                  <span>Featured</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <IoCalendarOutline className={`w-4 h-4 ${mutedTextColor}`} />
                <span className={mutedTextColor}>{formatDate(achievement.achievement_date)}</span>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs ${categoryStyle.bg} ${categoryStyle.text}`}>
                {achievement.category}
              </div>
              <div className="flex items-center gap-1">
                {getImpactStars()}
              </div>
            </div>
          </div>

          {/* Actions Menu */}
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className={`p-2 rounded-lg ${hoverBg} transition-colors`}
            >
              <IoEllipsisVertical className={`w-5 h-5 ${mutedTextColor}`} />
            </button>

            {showMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`absolute right-0 top-10 z-20 w-48 ${cardBg} border ${borderColor} rounded-lg shadow-lg overflow-hidden`}
                >
                  <button
                    onClick={() => {
                      onToggleFeatured();
                      setShowMenu(false);
                    }}
                    className={`w-full px-4 py-2 text-left ${textColor} ${hoverBg} transition-colors flex items-center gap-2`}
                  >
                    {achievement.is_featured ? <IoStarOutline /> : <IoStar />}
                    <span>{achievement.is_featured ? 'Unfeature' : 'Feature'}</span>
                  </button>
                  <button
                    onClick={() => {
                      onEdit();
                      setShowMenu(false);
                    }}
                    className={`w-full px-4 py-2 text-left ${textColor} ${hoverBg} transition-colors flex items-center gap-2`}
                  >
                    <IoCreateOutline />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => {
                      onDelete();
                      setShowMenu(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-red-600 dark:text-red-400 ${hoverBg} transition-colors flex items-center gap-2`}
                  >
                    <IoTrashOutline />
                    <span>Delete</span>
                  </button>
                </motion.div>
              </>
            )}
          </div>
        </div>

        {/* STAR Preview or Full Content */}
        {!isExpanded ? (
          <div className={`text-sm ${mutedTextColor} space-y-1`}>
            <p className="line-clamp-2">
              <span className="font-medium">Situation:</span> {achievement.situation}
            </p>
            <button
              onClick={() => setIsExpanded(true)}
              className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 flex items-center gap-1"
            >
              <IoExpandOutline className="w-4 h-4" />
              <span>Show full STAR story</span>
            </button>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`text-sm ${textColor} space-y-3`}
          >
            <div>
              <h4 className="font-medium text-purple-600 dark:text-purple-400 mb-1">Situation</h4>
              <p className={mutedTextColor}>{achievement.situation}</p>
            </div>
            <div>
              <h4 className="font-medium text-purple-600 dark:text-purple-400 mb-1">Task</h4>
              <p className={mutedTextColor}>{achievement.task}</p>
            </div>
            <div>
              <h4 className="font-medium text-purple-600 dark:text-purple-400 mb-1">Action</h4>
              <p className={mutedTextColor}>{achievement.action}</p>
            </div>
            <div>
              <h4 className="font-medium text-purple-600 dark:text-purple-400 mb-1">Result</h4>
              <p className={mutedTextColor}>{achievement.result}</p>
            </div>

            {achievement.key_skills && achievement.key_skills.length > 0 && (
              <div>
                <h4 className="font-medium text-purple-600 dark:text-purple-400 mb-1">Key Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {achievement.key_skills.map((skill, idx) => (
                    <span
                      key={idx}
                      className={`px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} ${mutedTextColor}`}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => setIsExpanded(false)}
              className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 flex items-center gap-1 mt-2"
            >
              <IoContractOutline className="w-4 h-4" />
              <span>Show less</span>
            </button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default AchievementCard;